// Email Phase 1 (Carla, 2026-10-21): a project's emails, from the viewer's own mailboxes (stored
// emails linked through the project's client by lib/emailLinks; a colleague's mailbox is never
// shown). Sits under "Notes linked to this project" and wears the same section style. No bodies.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { ErrorState, Skeleton } from '../../ui';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const who = (e) => (e.direction === 'outbound' ? `To ${e.toEmails || 'them'}` : `From ${e.fromName || e.fromEmail || 'them'}`);

export default function ProjectEmails({ projectId }) {
  const [state, setState] = useState({ status: 'loading', emails: [], error: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const d = await (await api(`/api/projects/${projectId}/emails`)).json();
      setState({ status: 'ready', emails: d.emails || [], error: null });
    } catch (err) {
      setState({ status: 'failed', emails: [], error: messageFromError(err, 'The emails for this project could not be loaded.') });
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="nt-linkednotes" aria-label="Emails about this project" data-testid="project-emails">
      <div className="nt-linkednotes__head"><h3 className="nt-linkednotes__title">Emails about this project</h3></div>
      {state.status === 'failed' ? (
        <ErrorState title="Emails didn't load" message={state.error} onRetry={load} />
      ) : state.status === 'loading' ? (
        <Skeleton lines={2} label="Loading the project's emails" />
      ) : !state.emails.length ? (
        <p className="nt-linkednotes__empty">No emails linked to this project yet. Emails with its client appear here.</p>
      ) : (
        <ul className="nt-linkednotes__list">
          {state.emails.map((e) => (
            <li key={e.id} className="nt-linkednotes__row">
              <span className="nt-linkednotes__name">{e.subject || '(no subject)'}</span>
              <span className="contacts-muted"> · {who(e)} · {fmt(e.sentAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
